import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'

export function useRanking() {
  const { ligaAtual, membroAtual } = useAuth()
  const [ranking, setRanking] = useState([])
  const [semanaAtual, setSemanaAtual] = useState(null)
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState(null)

  useEffect(() => {
    if (!ligaAtual?.id) return
    async function carregar() {
      setLoading(true)

      // Prioridade 1: semana com picks abertos
      // Prioridade 2: semana mais recente revelada (picks fechados mas revelados)
      // Prioridade 3: semana mais recente por número
      const { data: semAberta } = await supabase
        .from('semanas')
        .select('id, numero, picks_abertos, picks_revelados, resultados_processados')
        .eq('liga_id', ligaAtual.id)
        .eq('picks_abertos', true)
        .order('numero', { ascending: true })
        .limit(1)
        .maybeSingle()
      let semana = semAberta
      if (!semana) {
        const { data: semRevelada } = await supabase
          .from('semanas')
          .select('id, numero, picks_abertos, picks_revelados, resultados_processados')
          .eq('liga_id', ligaAtual.id)
          .eq('picks_revelados', true)
          .order('numero', { ascending: false })
          .limit(1)
          .maybeSingle()
        semana = semRevelada
      }
      if (!semana) {
        const { data: semRecente } = await supabase
          .from('semanas')
          .select('id, numero, picks_abertos, picks_revelados, resultados_processados')
          .eq('liga_id', ligaAtual.id)
          .order('numero', { ascending: false })
          .limit(1)
          .maybeSingle()
        semana = semRecente
      }

      setSemanaAtual(semana)

      const { data, error } = await supabase
        .from('ranking_liga')
        .select('*')
        .eq('liga_id', ligaAtual.id)

      if (error) { setErro('Erro ao carregar ranking.'); setLoading(false); return }

      let rankingData = data || []

      // Para o próprio usuário, complementa spread_medio e vidas_medias
      // usando picks processados da liga (não só o que a view já calcula)
      if (membroAtual?.id) {
        const myIdx = rankingData.findIndex(p => p.membro_id === membroAtual.id)
        if (myIdx !== -1) {
          // Busca picks processados do usuário nesta liga
          // Inclui picks com spread congelado, independente de ter resultado
          const { data: meusPicks } = await supabase
            .from('picks')
            .select('spread_congelado, resultado, semanas(tipo, liga_id, picks_revelados)')
            .eq('membro_id', membroAtual.id)
            .not('spread_congelado', 'is', null)

          const picksLiga = (meusPicks || []).filter(p => p.semanas?.liga_id === ligaAtual.id)
          const regular = picksLiga.filter(p =>
            p.semanas?.tipo === 'regular' &&
            p.spread_congelado !== null &&
            p.semanas?.picks_revelados === true
          )
          const spreadMedio = regular.length
            ? parseFloat((regular.reduce((s, p) => s + Number(p.spread_congelado), 0) / regular.length).toFixed(1))
            : null

          // Vidas médias via historico_vidas
          const { data: hv } = await supabase
            .from('historico_vidas')
            .select('vidas, semanas(liga_id)')
            .eq('membro_id', membroAtual.id)

          const hvLiga = (hv || []).filter(h => h.semanas?.liga_id === ligaAtual.id)
          // Se não há histórico processado, vidas médias = vidas atuais (posição corrente)
          const myEntry = rankingData[myIdx]
          const vidasMedias = hvLiga.length
            ? parseFloat((hvLiga.reduce((s, h) => s + h.vidas, 0) / hvLiga.length).toFixed(1))
            : (myEntry?.vidas_restantes ?? null)

          // Sobrescreve só se tiver dado mais atualizado
          rankingData = rankingData.map((p, i) =>
            i === myIdx
              ? {
                  ...p,
                  spread_medio: spreadMedio ?? p.spread_medio,
                  vidas_medias: vidasMedias ?? p.vidas_medias,
                }
              : p
          )
        }
      }

      // Fallback: quem não tem vidas_medias na view usa vidas_restantes
      rankingData = rankingData.map(p => ({
        ...p,
        vidas_medias: p.vidas_medias ?? p.vidas_restantes,
      }))

      setRanking(rankingData)
      setLoading(false)
    }
    carregar()

    const channel = supabase.channel('ranking-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'membros_liga' }, () => carregar())
      .subscribe()
    return () => supabase.removeChannel(channel)
  }, [ligaAtual?.id, membroAtual?.id])

  return { ranking, semanaAtual, loading, erro }
}
